import { useMemo, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { format, subDays, startOfDay } from "date-fns";

const CATEGORY_EMOJI: Record<string, string> = {
  "Akademisk mestring": "📚",
  "Fysisk helse": "🏃",
  "Egenomsorg": "🧼",
  "Kreativ utfoldelse": "🎨",
  "Sosial kompetanse": "🤝",
  "Ansvar hjemme": "🏠",
  "Selvstendighet": "💪",
  "Personlig utvikling": "🌱",
  "Annet": "✨",
};

interface Props {
  childId: string;
  familyId: string;
  childCreatedAt: string;
}

export function HabitOverviewCard({ childId, familyId, childCreatedAt }: Props) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const periodStart = useMemo(() => subDays(today, 14), [today]);
  

  const { data: taskItems, isLoading } = useQuery({
    queryKey: ["report-habits", childId, today.toISOString()],
    queryFn: async () => {
      const startStr = format(periodStart, "yyyy-MM-dd");
      const endStr = format(today, "yyyy-MM-dd");
      const { data, error } = await supabase
        .rpc("get_report_tasks", {
          _child_id: childId,
          _start_date: startStr,
          _end_date: endStr,
        });
      if (error) throw error;
      return data;
    },
  });

  const queryClient = useQueryClient();
  const backfillRan = useRef(false);

  // Trigger server-side classification for unclassified tasks (reads titles in backend, no privacy issue)
  useEffect(() => {
    if (!taskItems || backfillRan.current) return;
    const OLD_CATEGORIES = ["Lekser", "Lesing", "Trening", "Rydding", "Kreativt", "Musikk", "Matlaging", "Helse"];
    const hasUnclassified = taskItems.some((i: any) => !i.category || i.category === "Annet" || OLD_CATEGORIES.includes(i.category));
    if (!hasUnclassified) return;
    backfillRan.current = true;

    const startStr = format(periodStart, "yyyy-MM-dd");
    const endStr = format(today, "yyyy-MM-dd");

    supabase.functions
      .invoke("backfill-categories", {
        body: { childId, startDate: startStr, endDate: endStr },
      })
      .then(({ data }) => {
        if (data?.classified > 0) {
          queryClient.invalidateQueries({ queryKey: ["report-habits", childId] });
          queryClient.invalidateQueries({ queryKey: ["report-tasks", childId] });
        }
      })
      .catch((err) => console.warn("Backfill classification failed:", err));
  }, [taskItems, childId, queryClient, periodStart, today]);

  const habits = useMemo(() => {
    if (!taskItems || taskItems.length === 0) return [];

    const map = new Map<string, { total: number; done: number }>();
    for (const item of taskItems) {
      const key = (item as any).category ?? "Annet";
      const entry = map.get(key) ?? { total: 0, done: 0 };
      entry.total++;
      if (item.status === "done") entry.done++;
      map.set(key, entry);
    }

    return Array.from(map.entries())
      .map(([category, stats]) => ({
        category,
        emoji: CATEGORY_EMOJI[category] ?? "✨",
        ...stats,
        percent: Math.round((stats.done / stats.total) * 100),
      }))
      .sort((a, b) => b.total - a.total);
  }, [taskItems]);

  if (isLoading) {
    return <Skeleton className="h-32 w-full rounded-xl" />;
  }

  if (habits.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-sm text-muted-foreground">
            Ingen oppgaver denne uken ennå.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Vaneoversikt</CardTitle>
        <p className="text-xs text-muted-foreground">
          Fullføringsrate siste 14 dager
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {habits.map((h) => (
          <div key={h.category} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="truncate max-w-[60%]">
                {h.emoji} {h.category}
              </span>
              <span className="text-muted-foreground">
                {h.done}/{h.total} ({h.percent}%)
              </span>
            </div>
            <Progress value={h.percent} className="h-2" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
